import { AxiosError } from 'axios'
import {
    UseQueryOptions,
    SetDataOptions,
    InvalidateQueryFilters,
    InvalidateOptions,
    Updater,
    useQueryClient,
    useQuery,
} from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import {
    TStoreServiceError,
    TStoreProfileProps,
    TStoreProfileResponse,
} from 'services/StoreService'
import { QueryCacheKeys } from 'enums'
import { StoreService } from 'services'
import * as userSelectors from 'containers/User/user-selectors'
import { getRequestError } from 'utils/helpers'

export type TFetchFnParams = TStoreProfileProps
export type TFetchFnError = AxiosError<TStoreServiceError, TFetchFnParams>
export type TError = [string, TFetchFnError]
export type TQueryKey = [string, TFetchFnParams]
export type TQueryFnOpts = UseQueryOptions<TStoreProfileResponse, TError, TStoreProfileResponse, TQueryKey>

const key = QueryCacheKeys.storeProfile

/**
 * Хук API получить профайл магазина
 */
export default function useFetchStoreProfile(params: TFetchFnParams, opts: TQueryFnOpts = {}) {
    const { t } = useTranslation()
    const user = useSelector(userSelectors.user)

    return useQuery([`${key}-${user.id}`, params], () => {
        return StoreService.fetchStoreProfile(params)
            .then(({ data }) => {
                return data
            })
            .catch((err) => {
                return Promise.reject([getRequestError(err) || t('update_error'), err])
            })
    }, opts)
}

export function useInvalidateStoreProfile() {
    const queryClient = useQueryClient()
    const user = useSelector(userSelectors.user)

    const invalidate = (
        params: TFetchFnParams,
        { exact = true, ...filters }: InvalidateQueryFilters = {},
        options: InvalidateOptions = {},
    ) => {
        return queryClient.invalidateQueries([`${key}-${user.id}`, params], { exact, ...filters }, options)
    }

    return {
        invalidate,
    }
}

/**
 * Operation with query cached data
 */
export function useQueriesDataStoreProfile() {
    const queryClient = useQueryClient()
    const user = useSelector(userSelectors.user)

    /**
     * Update cached data of multiple queries
     */
    const setQueryData = (
        params: TFetchFnParams,
        updater: Updater<TStoreProfileResponse | undefined, TStoreProfileResponse | undefined>,
        options?: SetDataOptions,
    ) => {
        return queryClient.setQueriesData([`${key}-${user.id}`, params], updater, options)
    }

    const remove = (params: TFetchFnParams, { exact = true, ...filters }: InvalidateQueryFilters = {}) => {
        return queryClient.removeQueries([`${key}-${user.id}`, params], { exact, ...filters })
    }

    return {
        setQueryData,
        remove,
    }
}
